import yaml from "js-yaml";

import { getSupabase } from "../supabase";

/**
 * Data layer for the Admin Console customer index (MER-195).
 *
 * Joins three sources into one CustomerRow list:
 *
 * - Supabase `tenants` table (staff JWT reads all rows via MER-186 RLS).
 * - Bundled `/manifests/<slug>-config.yaml` (per-customer state:
 *   tier, services, status; copied at build time by
 *   scripts/copy-manifests.mjs).
 * - `service_event.last_updated_date` (proxy for last sync recency
 *   until a dedicated sync_log table lands; tracked as a v2 follow-up).
 *
 * `last_login_per_tenant` returns null in v1. Surfacing
 * `auth.users.last_sign_in_at` requires either a public view or a
 * SECURITY DEFINER function on top of `auth`; tracked as a v2
 * follow-up so staff have something usable today.
 */

export type Tier = "growth" | "professional" | "enterprise";

export interface CustomerRow {
  tenantId: string;
  slug: string;
  name: string;
  tier: Tier | null;
  enabledServices: string[];
  status: string | null;
  /** ISO timestamp of the most recent service_event row for this tenant. */
  lastSyncAt: string | null;
  /** ISO timestamp; null until the SECURITY DEFINER function lands. */
  lastLoginAt: string | null;
  lastLoginUserName: string | null;
}

interface TenantRow {
  id: string;
  slug: string;
  name: string;
}

interface ConfigYaml {
  tier?: string;
  enabled_services?: string[];
  status?: string;
}

const TIER_VALUES: ReadonlySet<string> = new Set(["growth", "professional", "enterprise"]);

function coerceTier(value: string | undefined | null): Tier | null {
  if (!value) return null;
  return TIER_VALUES.has(value) ? (value as Tier) : null;
}

async function fetchConfigForSlug(slug: string): Promise<ConfigYaml | null> {
  try {
    const resp = await fetch(`/manifests/${slug}-config.yaml`);
    if (!resp.ok) return null;
    const text = await resp.text();
    const parsed = yaml.load(text);
    if (parsed && typeof parsed === "object") return parsed as ConfigYaml;
    return null;
  } catch {
    return null;
  }
}

async function fetchLastSyncForTenant(tenantId: string): Promise<string | null> {
  // Calls the last_sync_per_tenant() helper from migration 0015,
  // which returns max(completed_at) where status='success' across
  // every sync_log row for the tenant. Replaces the original
  // service_event.last_updated_date proxy: that one reflected when
  // SOURCE rows were last modified in the ERM, not when Meridian
  // last upserted to Supabase, so it skewed stale-toward-now.
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("last_sync_per_tenant", {
    p_tenant_id: tenantId,
  });
  if (error) return null;
  // The RPC returns a single timestamptz scalar (or null when no
  // successful sync has landed for the tenant). Supabase wraps
  // scalar RPCs as the value itself.
  if (data === null || data === undefined) return null;
  if (typeof data === "string") return data;
  return null;
}

interface LastLoginRow {
  user_name: string | null;
  last_sign_in_at: string | null;
}

async function fetchLastLoginForTenant(
  tenantId: string,
): Promise<{ at: string | null; userName: string | null }> {
  // Calls the SECURITY DEFINER RPC added in migration 0014. The
  // function is staff-gated server-side so non-staff sessions get an
  // empty array back regardless of which tenant id they pass.
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("last_login_per_tenant", {
    p_tenant_id: tenantId,
  });
  if (error || !data) return { at: null, userName: null };
  const rows = data as LastLoginRow[];
  if (!Array.isArray(rows) || rows.length === 0) return { at: null, userName: null };
  const row = rows[0];
  return { at: row.last_sign_in_at ?? null, userName: row.user_name ?? null };
}

export async function listCustomers(): Promise<CustomerRow[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("tenants")
    .select("id, slug, name")
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  const tenants = (data as TenantRow[] | null) ?? [];

  // Parallelize config fetch + sync probe + login RPC per tenant.
  // Three HTTP / PostgREST calls per row, but the list is short
  // (~5 customers expected at the time MER-195 ships) so this is fine.
  const rows = await Promise.all(
    tenants.map(async (t): Promise<CustomerRow> => {
      const [config, lastSyncAt, lastLogin] = await Promise.all([
        fetchConfigForSlug(t.slug),
        fetchLastSyncForTenant(t.id),
        fetchLastLoginForTenant(t.id),
      ]);
      return {
        tenantId: t.id,
        slug: t.slug,
        name: t.name,
        tier: coerceTier(config?.tier),
        enabledServices: Array.isArray(config?.enabled_services)
          ? (config?.enabled_services as string[])
          : [],
        status: typeof config?.status === "string" ? config.status : null,
        lastSyncAt,
        lastLoginAt: lastLogin.at,
        lastLoginUserName: lastLogin.userName,
      };
    }),
  );
  return rows;
}
